<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'BulkContractServiceEditor',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    items: {
      type: Array,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const getPayload = (...args) => {
    const value = item.value.getChanges();
    return _.map(props.items, (p) => ({ id: p.id, value }));
  };

  const formId = ref(null);
  const labelAlign = ref('right');
  const item = ref(null);
  const currencies = ref(null);
  const services = ref(null);
  const invoiceGroups = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    noPrimaryButton: true,
    closeButtonText: $fn.value.gettext('Close'),
    size: 'lg',
  }));
  const b_3054425420812800_multiple = computed(() => false);

  const $data = computed(() => ({
    formId: formId.value,
    labelAlign: labelAlign.value,
    item: item.value,
    currencies: currencies.value,
    services: services.value,
    invoiceGroups: invoiceGroups.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_3054425420812800_multiple: b_3054425420812800_multiple.value,
  }));

  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };
  const $$load_item = () => {
    return $fn.value.schemaDefaults('CreateContractService', {
      asResource: true,
    });
  };
  const $$load_currencies = () => {
    return $fn.value.fetch('list_currency');
  };
  const $$load_services = () => {
    return $fn.value
      .fetch('suggest_product', {
        parameters: {
          query: '*',
          top: 50,
          skip: 0,
          product_types: 'service',
          product_invoice_groups: 'all',
        },
      })
      .then((data) => _.orderBy(data, [(p) => p.name.toLowerCase()]));
  };
  const $$load_invoiceGroups = () => {
    return $fn.value.fetch('list_product_invoice_group');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formId' },
      { name: 'labelAlign' },
      { name: 'item' },
      { name: 'currencies' },
      { name: 'services' },
      { name: 'invoiceGroups' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_3054425420812800_multiple', nodeId: '3054425420812800' },
    ],
    loaders: {
      formId: $$load_formId,
      item: $$load_item,
      currencies: $$load_currencies,
      services: $$load_services,
      invoiceGroups: $$load_invoiceGroups,
    },
    ux: {},
    dataRefs: {
      formId,
      labelAlign,
      item,
      currencies,
      services,
      invoiceGroups,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_3054425420812800_multiple,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext(
        'BulkContractServiceEditor',
        'Edit Service Properties'
      ),
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.items,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('items');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_v1rip6ze7_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeModal_h4qbvdtz4_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        close: true,
        ok: true,
        value: $event.data.response.data,
      },
    };
  };
  const act_1040167445267876_request_i5j6pgo5i_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'bulk_update_contract_service',
      data: getPayload(),
    };
  };
  const act_5731695935601903_setData_ezxt61hdz_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item.name',
      value: $event.data.option.name,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_5731695935601903_setData_gdxc8xb04_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item.service_id',
      value: $event.data.option.id,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_5731695935601903_setData_muom4mt8b_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'feeItem.unit',
      value: $event.data.option.unit,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };

  const act_7315092382398562_closeModal_v1rip6ze7 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_v1rip6ze7_getActionArgs,
    });
  const inst_evh_7315092382398562_close_n7fv4aqsx =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_v1rip6ze7,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_n7fv4aqsx],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_svgu2zp46 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_90ehmt22u =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_svgu2zp46,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_90ehmt22u],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_nsiincz0h =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_6gn3l1glg =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_nsiincz0h,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_6gn3l1glg],
    event: 'close',
  });
  const act_1040167445267876_closeModal_h4qbvdtz4 =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_h4qbvdtz4_getActionArgs,
    });
  const inst_evh_1040167445267876_success_3tazu4lls =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_h4qbvdtz4,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_1040167445267876_request_i5j6pgo5i = new actionTypes.RequestAction({
    actionArgs: act_1040167445267876_request_i5j6pgo5i_getActionArgs,
    displayName: 'save',
    events: [inst_evh_1040167445267876_success_3tazu4lls],
  });
  const inst_evh_1040167445267876_submit_6w78ce6nv =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_request_i5j6pgo5i,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_6w78ce6nv],
    event: 'submit',
  });
  const act_5731695935601903_setData_ezxt61hdz = new actionTypes.SetDataAction({
    actionArgs: act_5731695935601903_setData_ezxt61hdz_getActionArgs,
  });
  const inst_evh_5731695935601903_optionSelected_k1ik8stss =
    new actionTypes.EventHandlerImpl({
      action: act_5731695935601903_setData_ezxt61hdz,
      event: 'optionSelected',
      displayName: 'setData',
    });
  const act_5731695935601903_setData_gdxc8xb04 = new actionTypes.SetDataAction({
    actionArgs: act_5731695935601903_setData_gdxc8xb04_getActionArgs,
  });
  const inst_evh_5731695935601903_optionSelected_ot40r3bqx =
    new actionTypes.EventHandlerImpl({
      action: act_5731695935601903_setData_gdxc8xb04,
      event: 'optionSelected',
      displayName: 'setData',
    });
  const act_5731695935601903_setData_muom4mt8b = new actionTypes.SetDataAction({
    actionArgs: act_5731695935601903_setData_muom4mt8b_getActionArgs,
  });
  const inst_evh_5731695935601903_optionSelected_5j5ooxs5y =
    new actionTypes.EventHandlerImpl({
      action: act_5731695935601903_setData_muom4mt8b,
      event: 'optionSelected',
      displayName: 'setData',
    });
  const inst_evh_5731695935601903_optionSelected =
    new actionTypes.EventHandlerGroup({
      handlers: [
        inst_evh_5731695935601903_optionSelected_k1ik8stss,
        inst_evh_5731695935601903_optionSelected_ot40r3bqx,
        inst_evh_5731695935601903_optionSelected_5j5ooxs5y,
      ],
      event: 'optionSelected',
    });
  const act_2434514588044777_closeComponent_y43xlgwat =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_heu0nqfig =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_y43xlgwat,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_heu0nqfig],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_n7fv4aqsx,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_90ehmt22u,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_6gn3l1glg,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_3tazu4lls,
    inst_evh_1040167445267876_submit_6w78ce6nv,
    save: inst_evh_1040167445267876_submit_6w78ce6nv,
    inst_evh_1040167445267876_submit,
    inst_evh_5731695935601903_optionSelected_k1ik8stss,
    inst_evh_5731695935601903_optionSelected_ot40r3bqx,
    inst_evh_5731695935601903_optionSelected_5j5ooxs5y,
    inst_evh_5731695935601903_optionSelected,
    inst_evh_2434514588044777_clickPreventStop_heu0nqfig,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_n7fv4aqsx = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_n7fv4aqsx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_90ehmt22u = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_90ehmt22u.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_6gn3l1glg = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_6gn3l1glg.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_3tazu4lls = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_3tazu4lls.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_6w78ce6nv = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_6w78ce6nv.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5731695935601903_optionSelected_k1ik8stss = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5731695935601903_optionSelected_k1ik8stss.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5731695935601903_optionSelected_ot40r3bqx = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5731695935601903_optionSelected_ot40r3bqx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5731695935601903_optionSelected_5j5ooxs5y = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5731695935601903_optionSelected_5j5ooxs5y.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5731695935601903_optionSelected = async (event, scope) => {
    return await actions.inst_evh_5731695935601903_optionSelected.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_heu0nqfig = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_heu0nqfig.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="BulkContractServiceEditor"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :id="formId"
        name="contract"
        class="row"
        ref="mainForm"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpCol>
          <ItpFormGroup
            name="service"
            :label="pgettext('ContractService', 'Service')"
            :label-cols-md="3"
            :content-cols-md="9"
            :labelAlign="labelAlign"
            :helpTooltip="
              pgettext(
                'ContractService|Tooltip',
                'Select the product or service the invoice line refers to.'
              )
            "
          >
            <ItpFormSearchableSelect
              name="client"
              v-model="item.service"
              bind-object
              :options="services"
              textField="name"
              @optionSelected="evh_5731695935601903_optionSelected($event, {})"
            >
            </ItpFormSearchableSelect>
          </ItpFormGroup>
          <ItpFormGroup
            :label-cols-md="3"
            :content-cols-md="9"
            name="invoice_group_id"
            :label="pgettext('ContractService', 'Invoice Group')"
            :labelAlign="labelAlign"
            :helpTooltip="
              pgettext(
                'ContractService|Tooltip',
                'The invoice group this service belongs to.'
              )
            "
          >
            <ItpFormSearchableSelect
              name="invoice_group_id"
              v-model="item.invoice_group_id"
              :multiple="b_3054425420812800_multiple"
              :options="invoiceGroups"
              valueField="id"
              textField="name"
              clearable
            >
            </ItpFormSearchableSelect>
          </ItpFormGroup>
          <ItpFormGroup
            :label-cols-md="3"
            name="intermediated_service"
            :label="pgettext('ContractService', 'Intermediated service')"
            :labelAlign="labelAlign"
            :helpTooltip="
              pgettext(
                'ContractService|Tooltip',
                'Select this option if the service is provided via an intermediary party.'
              )
            "
          >
            <ItpFormCheckbox
              name="intermediated_service"
              v-model="item.intermediated_service"
            >
            </ItpFormCheckbox>
          </ItpFormGroup>
          <ItpFormGroup
            name="valid_to"
            :label="pgettext('ContractService', 'Description')"
            :label-cols-md="3"
            :content-cols-md="9"
            :labelAlign="labelAlign"
            :helpTooltip="
              pgettext(
                'ContractService|Tooltip',
                'Internal notes for reference only. Not visible externally.'
              )
            "
          >
            <ItpFormTextarea
              name="description"
              v-model="item.description"
              :rows="5"
            >
            </ItpFormTextarea>
          </ItpFormGroup>
          <hr />
          <fieldset>
            <legend>
              {{ pgettext('ContractServiceEditor|Section', 'Invoicing') }}
            </legend>
            <ItpFormGroup
              name="name"
              :label="pgettext('ContractService', 'Name')"
              :label-cols-md="3"
              :content-cols-md="9"
              :labelAlign="labelAlign"
              :helpTooltip="
                pgettext(
                  'ContractService|Tooltip',
                  'This text will appear on the invice line.'
                )
              "
            >
              <ItpFormInput name="name" v-model="item.name"> </ItpFormInput>
            </ItpFormGroup>
            <ItpFormGroup
              name="invoice_notes"
              :label="pgettext('ContractServiceFee', 'Invoice notes')"
              :label-cols-md="3"
              :content-cols-md="9"
              :labelAlign="labelAlign"
              :helpTooltip="
                pgettext(
                  'ContractService|Tooltip',
                  'Note to display on the invoice line.'
                )
              "
            >
              <ItpFormTextarea name="fee_notes" v-model="item.invoice_notes">
              </ItpFormTextarea>
            </ItpFormGroup>
            <ItpFormGroup
              name="billing_frequency"
              :label="pgettext('ContractService', 'Billing frequency')"
              :label-cols-md="3"
              :content-cols-md="3"
              :labelAlign="labelAlign"
              :helpTooltip="
                pgettext(
                  'ContractService|Tooltip',
                  'The cycle length of issuing the invoices.'
                )
              "
            >
              <BillingFrequencySelector v-model="item.billing_frequency">
              </BillingFrequencySelector>
            </ItpFormGroup>
            <ItpFormGroup
              name="billing_date_rule"
              :label="pgettext('ContractService', 'Billing date rule')"
              :label-cols-md="3"
              :content-cols-md="9"
              :labelAlign="labelAlign"
              :helpTooltip="
                pgettext(
                  'ContractService|Tooltip',
                  'The cycle length of issuing the invoices.'
                )
              "
            >
              <BillingDateRuleFields v-model="item.billing_date_rule">
              </BillingDateRuleFields>
            </ItpFormGroup>
            <ItpFormGroup :label-cols-md="3" :content-cols-md="9">
              <hr class="dots" />
            </ItpFormGroup>
            <ItpFormGroup
              name="due_date_rule"
              :label="pgettext('ContractService', 'Due date rule')"
              :label-cols-md="3"
              :content-cols-md="9"
              :labelAlign="labelAlign"
              :helpTooltip="
                pgettext(
                  'ContractService|Tooltip',
                  'The rule of calculating the payment dates.'
                )
              "
            >
              <DueDateRuleFields v-model="item.due_date_rule">
              </DueDateRuleFields>
            </ItpFormGroup>
            <ItpFormGroup :label-cols-md="3" :content-cols-md="9">
              <hr class="dots" />
            </ItpFormGroup>
            <ItpFormGroup
              name="billing_currency"
              :label="pgettext('ContractService', 'Billing currency')"
              :label-cols-md="3"
              :content-cols-md="3"
              :labelAlign="labelAlign"
              :helpTooltip="
                pgettext(
                  'ContractService|Tooltip',
                  'The currency to use on the invoice.'
                )
              "
            >
              <BillingCurrencySelector v-model="item.billing_currency">
              </BillingCurrencySelector>
            </ItpFormGroup>
            <ItpFormGroup
              name="exchange_rate_source"
              :label="pgettext('ContractService', 'Exchange rate source')"
              :label-cols-md="3"
              :content-cols-md="3"
              :labelAlign="labelAlign"
              :helpTooltip="
                pgettext(
                  'ContractService|Tooltip',
                  'The currency exchange rate\'s provider.'
                )
              "
            >
              <ExchangeRateSourceSelector v-model="item.exchange_rate_source">
              </ExchangeRateSourceSelector>
            </ItpFormGroup>
            <ItpFormGroup
              name="exchange_rate_date_rule"
              :label="pgettext('ContractService', 'Exchange rate date rule')"
              :label-cols-md="3"
              :content-cols-md="9"
              :labelAlign="labelAlign"
              :helpTooltip="
                pgettext(
                  'ContractService|Tooltip',
                  'The date used to determine the exchange rate for billing purposes.'
                )
              "
            >
              <ExchangeRateDateRuleFields
                v-model="item.exchange_rate_date_rule"
              >
              </ExchangeRateDateRuleFields>
            </ItpFormGroup>
          </fieldset>
        </ItpCol>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('Button', 'Save')"
          :disabled="!forms.contract || !forms.contract.submittable"
          type="submit"
          :form="formId"
          variant="primary"
          icon="fa-save"
          :spinning="actions.save.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
